"use client";

/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { ChevronDown, Menu, Phone, ShoppingCart } from "lucide-react";
import { Suspense } from "react";
import { BranchSwitcher } from "./BranchSwitcher.jsx";
import { useBranchConfig } from "../../hooks/useBranchConfig.js";
import { useMenuConfig } from "../../hooks/useMenuConfig.js";
import { withLocaleHref as localeHref } from "@kkeut/lib/locale-url";
import { IdentityStatusSlot } from "./IdentityStatusSlot.jsx";

/* ═══════════════ 내부 유틸 ═══════════════ */
const LOCALE_RE = /^\/(ko|en|ja|zh|th)(?=\/|$)/;
const EXTERNAL_RE = /^(https?:)?\/\//i;

function isExternal(href = "") {
  return EXTERNAL_RE.test(href) || href.startsWith("tel:") || href.startsWith("mailto:");
}

// 메뉴 링크(관리자에서 입력)에 언어를 붙인다. 규칙은 공용 locale-url 과 같다 —
// 외국어일 때 /main/X 를 /{lang}/X 로 (전에는 /en/main/myTickets 로 가서 404).
function withLocaleHref(href = "/", locale = "ko") {
  if (isExternal(href)) return href;
  const p = href.startsWith("/") ? href : `/${href}`;
  if (p.startsWith("/admin")) return p;
  return localeHref(p, locale);
}

function labelOf(label, locale) {
  if (!label) return "";
  if (typeof label === "string") return label;
  const v = label[locale] ?? label["ko"] ?? Object.values(label)[0] ?? "";
  if (locale === "en" && !label["en"] && label["ko"]) return "";
  return v;
}

function isMemberPreviewPath(pathname = "/") {
  const normalized = (pathname || "/").replace(LOCALE_RE, "") || "/";
  return /^\/(main\/)?(login|mypage)(\/|$)/.test(normalized);
}

/**
 * 상단에 어두운 히어로 없이 흰 배경으로 시작하는 페이지.
 * 스크롤 전 헤더는 흰 글자라 이런 페이지에서는 글자가 배경에 묻힌다.
 * → 처음부터 흰 배경·검은 글자(compact) 헤더로 띄운다.
 */
function isLightBackgroundPath(pathname = "/") {
  const normalized = (pathname || "/").replace(LOCALE_RE, "") || "/";
  // 하위 페이지는 상단 사진 없이 흰 바탕으로 시작한다 — 홈(사진 위 흰 글자 헤더)만 제외
  return !/^\/(main\/?)?$/.test(normalized);
}

/* ═══════════════ 모바일 헤더 ═══════════════ */
/**
 * @param {object}   props
 * @param {Function} props.cartState           - zustand store (s => s.cart)
 * @param {Function} props.onMobileMenuToggle
 * @param {ReactNode} props.languageSwitcherSlot
 * @param {ReactNode} [props.userIconSlot]
 * @param {string}   [props.logoAlt]           - 로고 이미지 alt 텍스트 (기본: "끗 한의원")
 */
export function MobileHeaderShell({
  cartState,
  onMobileMenuToggle,
  languageSwitcherSlot,
  userIconSlot,
  logoAlt = "끗 한의원",
  branches = null, // 서버에서 미리 읽은 지점 목록 (#961), 없으면 드롭다운이 열 때 읽는다
}) {
  const branchId = process.env.NEXT_PUBLIC_BRANCH_ID || "";
  const { phone } = useBranchConfig(branchId);

  const pathname = usePathname();
  const effectiveLocale = LOCALE_RE.exec(pathname || "")?.[1] || "ko";
  const isMemberPreview = isMemberPreviewPath(pathname);

  const [isScrolled, setIsScrolled] = useState(false);
  const cartCount = cartState?.((s) => s.cart?.length || 0) ?? 0;
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
      <header
        className={`fixed z-50 flex w-full items-center justify-between gap-2 p-3 transition-all duration-200 lg:hidden ${
          isMemberPreview
            ? "border-b border-[#ead9df] bg-white/95 shadow-[0_8px_28px_rgba(91,58,68,0.08)] backdrop-blur-xl"
            : isScrolled
              ? "bg-white/90 shadow-md"
              : "bg-transparent"
        }`}
      >
        {/* 왼쪽: 로고 + 바로 옆 지점 이동 알약 (로고와 같은 높이) — 한 줄로 얇게 */}
        <div className="flex min-w-0 items-center gap-1.5">
          <Link href={withLocaleHref("/", effectiveLocale)} className="shrink-0">
            <Image
              src="/assets/withTitleLogo.png"
              width={468}
              height={89}
              alt={logoAlt}
              className="h-auto w-[120px]"
            />
          </Link>
          <BranchSwitcher branchId={branchId} locale={effectiveLocale} initialDocs={branches} mobile />
        </div>

        {/* 오른쪽: 언어 · 전화 · 장바구니 · 메뉴 */}
        <div className="flex shrink-0 items-center gap-1">
          <Suspense fallback={<div className="w-5 h-5" />}>
            {languageSwitcherSlot}
          </Suspense>
          {phone && (
            <a href={`tel:${phone}`} className="p-1" aria-label="전화하기">
              <Phone size={20} suppressHydrationWarning />
            </a>
          )}
          <Link
            href={withLocaleHref("/cart", effectiveLocale)}
            className="relative p-1"
            aria-label="장바구니"
          >
            <ShoppingCart size={20} suppressHydrationWarning />
            {cartCount > 0 && (
              <span className="absolute -right-1 -top-1 rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </Link>
          {/* 인증 해제 — 장바구니 옆. 데스크톱 고정 메뉴와 같은 자리다 (#1114). */}
          <IdentityStatusSlot size={20} />
          {userIconSlot}
          <button className="p-1" onClick={onMobileMenuToggle} aria-label="메뉴 열기">
            <Menu size={20} color="black" suppressHydrationWarning />
          </button>
        </div>
      </header>
  );
}

/* ═══════════════ 데스크톱 헤더 ═══════════════ */
/**
 * @param {object}    props
 * @param {Array}     [props.initialMenu]      - SSR fallback (선택)
 * @param {Function}  props.cartState           - zustand store
 * @param {ReactNode} props.languageSwitcherSlot
 * @param {ReactNode} [props.userIconSlot]
 * @param {boolean}   [props.showTopUserIcon]
 * @param {string}    [props.logoAlt]           - 로고 이미지 alt 텍스트 (기본: "끗 한의원")
 */
export function DesktopHeaderShell({
  initialMenu = [],
  useInitialMenu = false,
  cartState,
  languageSwitcherSlot,
  userIconSlot,
  showTopUserIcon = false,
  logoAlt = "끗 한의원",
  branches = null,
}) {
  const branchId = process.env.NEXT_PUBLIC_BRANCH_ID || "";
  const { menuItems: fetchedItems } = useMenuConfig(useInitialMenu ? null : branchId);

  const pathname = usePathname();
  const effectiveLocale = LOCALE_RE.exec(pathname || "")?.[1] || "ko";
  const isMemberPreview = isMemberPreviewPath(pathname);

  const [isScrolled, setIsScrolled] = useState(false);
  const cartCount = cartState?.((s) => s.cart?.length || 0) ?? 0;
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const menuItems = fetchedItems.length ? fetchedItems : initialMenu;
  const rootItems = menuItems.filter((m) => !m.parent);
  const childItems = menuItems.filter((m) => m.parent);
  const showCompactHeader = isScrolled || isMemberPreview || isLightBackgroundPath(pathname);

  const hrefOf = (it) =>
    isExternal(it.href) ? it.href : withLocaleHref(it.href || "/", effectiveLocale);

  return (
    <>
      {/* ── 스크롤 전 (세로) ── */}
      {!showCompactHeader && (
        <div className="fixed top-0 z-40 hidden w-full flex-col items-center pt-16 text-white lg:flex">
          {/* 지점 선택은 메뉴 줄이 아니라 로고 옆에 — 로고는 가운데 그대로 */}
          <div className="relative">
            <Link href={withLocaleHref("/", effectiveLocale)} className="flex justify-center">
              <Image
                src="/assets/withTitleLogo.png"
                width={468}
                height={89}
                alt={logoAlt}
                className="w-[260px] h-auto"
              />
            </Link>
            {/* z-50: 펼친 지점 목록이 아래 메뉴 줄 위로 올라오게 (transform 이 새 층을 만들어 뒤의 메뉴에 깔렸다) */}
            <div className="absolute left-full top-1/2 z-50 ml-5 -translate-y-1/2">
              <BranchSwitcher branchId={branchId} locale={effectiveLocale} initialDocs={branches} logoTone="light" />
            </div>
          </div>

          <div className="mt-10 flex items-center gap-12 text-xl font-semibold tracking-wide xl:gap-16">
            {rootItems.map((item) => {
              const subs = childItems.filter((c) => c.parent === item.id);
              const label = labelOf(item?.label ?? "", effectiveLocale) || "";

              if (subs.length) {
                return (
                  <div key={item.id} className="relative group">
                    <span className="flex cursor-pointer items-center gap-0.5">
                      {label}
                      <ChevronDown size={16} aria-hidden="true" className="transition-transform group-hover:rotate-180" />
                    </span>
                    <div className="invisible absolute left-1/2 top-full mt-2 -translate-x-1/2 transform rounded-xl bg-white/90 px-4 py-3 text-black opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:opacity-100">
                      {subs.map((c) => {
                        const childLabel = labelOf(c.label, effectiveLocale);
                        const href = hrefOf(c);
                        return isExternal(href) ? (
                          <a key={c.id} href={href} target="_blank" rel="noopener noreferrer"
                            className="px-4 py-2 text-sm whitespace-nowrap hover:text-gray-700 block">
                            {childLabel}
                          </a>
                        ) : (
                          <Link key={c.id} href={href}
                            className="px-4 py-2 text-sm whitespace-nowrap hover:text-gray-700 block">
                            {childLabel}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              const href = hrefOf(item);
              return isExternal(href) ? (
                <a key={item.id} href={href} target="_blank" rel="noopener noreferrer"
                  className="hover:text-gray-300">{label}</a>
              ) : (
                <Link key={item.id} href={href} className="hover:text-gray-300">{label}</Link>
              );
            })}
            {showTopUserIcon && userIconSlot}
            {languageSwitcherSlot}
          </div>
        </div>
      )}

      {/* ── 스크롤 후 (가로) ── */}
      {showCompactHeader && (
        <div
          className={`fixed top-0 z-50 hidden w-full transition-all duration-200 lg:flex ${
            isMemberPreview
              ? "border-b border-[#ead9df] bg-white/88 shadow-[0_12px_40px_rgba(91,58,68,0.08)] backdrop-blur-xl"
              : "bg-white/90 shadow-md"
          }`}
        >
          <div className="mx-auto flex w-full max-w-[1320px] items-center justify-between px-6 py-4">
            <div className="flex flex-shrink-0 items-center gap-4">
              <Link href={withLocaleHref("/", effectiveLocale)} className="flex-shrink-0">
                <Image
                  src="/assets/withTitleLogo.png"
                  width={468}
                  height={89}
                  alt={logoAlt}
                  className={isMemberPreview ? "h-auto w-[180px] xl:w-[210px]" : "h-auto w-[180px] xl:w-[220px]"}
                />
              </Link>
              <BranchSwitcher branchId={branchId} locale={effectiveLocale} initialDocs={branches} logoTone="dark" />
            </div>

            <nav
              className={`flex items-center ${
                isMemberPreview
                  ? "space-x-5 text-[17px] font-semibold text-[#2f2428] xl:space-x-8 xl:text-[18px]"
                  : "space-x-4 text-[17px] font-medium text-black xl:space-x-6 xl:text-[19px]"
              }`}
            >
              {rootItems.map((item) => {
                const subs = childItems.filter((c) => c.parent === item.id);
                const label = labelOf(item.label, effectiveLocale);

                if (subs.length) {
                  return (
                    <div key={item.id} className="relative group">
                      <span className="flex cursor-pointer items-center gap-0.5">
                        {label}
                        <ChevronDown size={16} aria-hidden="true" className="transition-transform group-hover:rotate-180" />
                      </span>
                      <div
                        className={`invisible absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 transform min-w-[180px] rounded-xl px-4 py-3 text-center opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:opacity-100 ${
                          isMemberPreview ? "border border-[#efdfe4] bg-white" : "bg-white/90"
                        }`}
                      >
                        {subs.map((c) => {
                          const childLabel = labelOf(c.label, effectiveLocale);
                          const href = hrefOf(c);
                          return isExternal(href) ? (
                            <a key={c.id} href={href} target="_blank" rel="noopener noreferrer"
                              className="inline-block px-4 py-2 text-sm whitespace-nowrap hover:text-gray-900">
                              {childLabel}
                            </a>
                          ) : (
                            <Link key={c.id} href={href}
                              className="inline-block px-4 py-2 text-sm whitespace-nowrap hover:text-gray-900">
                              {childLabel}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const href = hrefOf(item);
                return isExternal(href) ? (
                  <a key={item.id} href={href} target="_blank" rel="noopener noreferrer"
                    className="hover:text-gray-600">{label}</a>
                ) : (
                  <Link key={item.id} href={href} className="hover:text-gray-600">{label}</Link>
                );
              })}
            </nav>

            <div className="flex items-center space-x-4 text-[#2f2428]">
              <Link
                href={withLocaleHref("/cart", effectiveLocale)}
                className="relative"
                aria-label="장바구니"
              >
                <ShoppingCart size={24} suppressHydrationWarning />
                {cartCount > 0 && (
                  <span className="absolute -right-2 -top-1 rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                    {cartCount}
                  </span>
                )}
              </Link>
              {/* 인증 해제 — 장바구니 옆. 로그인 아이콘은 지점이 켜야 보이지만
                  (showAuthIcon 기본 false), 인증 해제는 인증된 동안 어디서나
                  보여야 한다. 상단 고정 메뉴에 두면 어느 페이지에서나 닿는다
                  (#1114). */}
              <IdentityStatusSlot size={24} />
              {userIconSlot}
              <Suspense fallback={<div className="w-5 h-5" />}>
                {languageSwitcherSlot}
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
